import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Heart, ArrowUpRight, X } from 'lucide-react'
import { getMyFavorites, removeFavorite, type FavoriteResult } from '@/services/favoriteService'
import { useFavoriteStore } from '@/store/favoriteStore'
import { gradientForCategory } from '@/components/ui/CourseCard'

export default function WishlistTab() {
  const [favorites, setFavorites] = useState<FavoriteResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const load = () => {
    setLoading(true)
    getMyFavorites()
      .then(setFavorites)
      .catch((err) => setError(err instanceof Error ? err.message : 'โหลดรายการโปรดไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleRemove = async (courseId: number) => {
    setFavorites((prev) => prev.filter((f) => f.course_id !== courseId))
    try {
      await removeFavorite(courseId)
      // sync store ด้วย (ใช้ตอน user ไปหน้า course list ต่อ ไอคอนหัวใจจะได้ถูกต้อง) — แก้ set ตรงๆ ไม่เรียก toggleFavorite ซ้ำ (จะยิง API ซ้ำ)
      useFavoriteStore.setState((s) => {
        const next = new Set(s.favoriteIds)
        next.delete(courseId)
        return { favoriteIds: next }
      })
    } catch {
      load()
    }
  }

  return (
    <div>
      <p className="mb-6 text-sm text-[#69738A]">คอร์สที่คุณบันทึกไว้ดูทีหลัง ({favorites.length})</p>

      {loading ? (
        <p className="text-sm text-[#69738A]">กำลังโหลด...</p>
      ) : error ? (
        <p className="text-sm text-rose-500">{error}</p>
      ) : favorites.length === 0 ? (
        <div className="border border-[#E7E7ED] bg-white p-12 text-center" style={{ borderRadius: 6 }}>
          <Heart className="mx-auto mb-3 h-8 w-8 text-[#69738A]/40" strokeWidth={1.5} />
          <p className="mb-4 text-[#69738A]">คุณยังไม่มีคอร์สในรายการโปรด</p>
          <Link to="/courses" className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline">
            ไปเลือกดูคอร์สทั้งหมด <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <div className="divide-y divide-[#E7E7ED] border border-[#E7E7ED] bg-white" style={{ borderRadius: 6 }}>
          {favorites.map((fav) => (
            <div key={fav.favorite_id} className="group flex items-center gap-5 p-5 transition-colors hover:bg-[#F7F6FB]">
              <Link
                to={`/courses/${fav.course.slug}`}
                className={`flex h-20 w-32 shrink-0 items-center justify-center overflow-hidden bg-gradient-to-br ${gradientForCategory(fav.course.category_id)}`}
                style={{ borderRadius: 4 }}
              >
                {fav.course.thumbnail_url && (
                  <img src={fav.course.thumbnail_url} alt={fav.course.title} className="h-full w-full object-cover" />
                )}
              </Link>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/courses/${fav.course.slug}`}
                  className="line-clamp-2 text-[15px] font-medium leading-snug text-[#19172A] transition-colors hover:text-accent"
                >
                  {fav.course.title}
                </Link>
                <p className="mt-2 text-sm font-semibold text-[#19172A]">
                  {fav.course.is_free ? 'ฟรี' : `฿${fav.course.price.toLocaleString()}`}
                </p>
              </div>
              <button
                onClick={() => handleRemove(fav.course_id)}
                className="flex shrink-0 items-center gap-1.5 border border-[#E7E7ED] px-3 py-2 text-xs font-medium text-[#69738A] transition-colors hover:border-rose-300 hover:text-rose-500"
                style={{ borderRadius: 4 }}
              >
                <X className="h-3.5 w-3.5" />
                เอาออก
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
